import React, {useCallback, useEffect, useRef, useState} from 'react';
import {formatDateTime} from '../utils/dateTime';
import {Link, useSearchParams} from 'react-router-dom';
import createAxiosInstance from '../support/AxiosSupport';
import AuthImage from '../components/AuthImage';
import {CartIcon, SparklesIcon} from '../components/icons';
import {useDocumentTitle} from '../hooks/useDocumentTitle';

const InlineSpinner: React.FC = () => (
    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
    </svg>
);

interface DetectedItem {category: string; color: string; style: string; season: string[]; formality: string; scenarios: string[]}
interface GapAnalysis {closesGap: boolean; gapName: string | null; confidence: number}
interface DuplicateMatch {description: string; similarity: number; reason: string}
interface DuplicateAnalysis {isDuplicate: boolean; duplicateScore: number; matchedItems: DuplicateMatch[]}
interface OutfitPotential {newOutfitsCount: number; compatibleItemsCount: number; bestScenarios: string[]}
interface RetirementCandidate {description: string; score: number; reason: string}
interface Recommendation {action: string; text: string}

interface AiPayload {
    detectedItem?: DetectedItem; gapAnalysis?: GapAnalysis;
    duplicateAnalysis?: DuplicateAnalysis; outfitPotential?: OutfitPotential;
    replacementRequired?: boolean; retirementCandidates?: RetirementCandidate[];
    recommendation?: Recommendation; wardrobeRoi?: number;
}

interface DuplicateItemRef {itemId: string; name: string | null}

interface AnalysisResult {
    id: string; imageUrl: string | null; comment: string | null;
    categoryHint: string | null; priceRub: number | null;
    purchaseVerdict: string | null; purchaseScore: number | null;
    summary: string | null; aiResponse: AiPayload | null; createdAt: string;
    // v2 (MR-4 purchase-ai-integration)
    pros?: string[]; cons?: string[];
    duplicateItems?: DuplicateItemRef[];
    budgetImpact?: string | null;
    suggestedCategoryId?: string | null;
    shoppingItemId?: string | null;
}

/** imageKey из imageUrl бэкенда (`/api/clothing/images/<key>`) для авторизованной загрузки AuthImage. */
export function imageKeyFromUrl(imageUrl: string | null): string | null {
    if (!imageUrl) return null;
    const prefix = '/api/clothing/images/';
    return imageUrl.startsWith(prefix) ? imageUrl.slice(prefix.length) : null;
}

/** aiResponse приходит строкой — разбираем в объект, битый JSON не роняет страницу. */
function normalizeResult(r: AnalysisResult): AnalysisResult {
    if (r.aiResponse && typeof r.aiResponse === 'string') {
        try { r.aiResponse = JSON.parse(r.aiResponse as unknown as string); } catch {}
    }
    return r;
}

const VERDICT_CONFIG: Record<string, {label: string; color: string; bg: string}> = {
    buy: {label: 'Покупать', color: 'text-green-700 dark:text-green-400', bg: 'bg-green-50 dark:bg-green-900/20'},
    buy_if_replace: {label: 'Покупать при замене старой', color: 'text-yellow-700 dark:text-yellow-400', bg: 'bg-yellow-50 dark:bg-yellow-900/20'},
    do_not_buy: {label: 'Не покупать', color: 'text-red-700 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-900/20'},
    need_more_data: {label: 'Нужно больше данных', color: 'text-ink-600 dark:text-ink-400', bg: 'bg-ink-50 dark:bg-ink-800'},
    bad_fit_risk: {label: 'Риск плохой посадки', color: 'text-orange-700 dark:text-orange-400', bg: 'bg-orange-50 dark:bg-orange-900/20'},
    duplicate: {label: 'Дубль', color: 'text-red-600 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-900/20'},
    wardrobe_gap: {label: 'Закрывает пробел', color: 'text-green-700 dark:text-green-400', bg: 'bg-green-50 dark:bg-green-900/20'},
};

/** Списки pros/cons полного разбора (v2): аргументы «за» и «против». */
function ProsConsSection({pros, cons}: {pros?: string[]; cons?: string[]}) {
    if (!pros?.length && !cons?.length) return null;
    return (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="pros-cons">
            {!!pros?.length && (
                <div className="bg-green-50 dark:bg-green-900/10 rounded-xl p-3">
                    <h3 className="text-sm font-semibold text-green-700 dark:text-green-400 uppercase tracking-wide mb-1.5">За</h3>
                    <ul className="space-y-1 text-sm text-ink-700 dark:text-ink-300 list-disc list-inside">
                        {pros.map((p, i) => <li key={i}>{p}</li>)}
                    </ul>
                </div>
            )}
            {!!cons?.length && (
                <div className="bg-red-50 dark:bg-red-900/10 rounded-xl p-3">
                    <h3 className="text-sm font-semibold text-red-700 dark:text-red-400 uppercase tracking-wide mb-1.5">Против</h3>
                    <ul className="space-y-1 text-sm text-ink-700 dark:text-ink-300 list-disc list-inside">
                        {cons.map((c, i) => <li key={i}>{c}</li>)}
                    </ul>
                </div>
            )}
        </div>
    );
}

/** Реальные дубли из гардероба (v2): карточки со ссылками на вещи. */
function DuplicateItemsSection({items}: {items?: DuplicateItemRef[]}) {
    if (!items?.length) return null;
    return (
        <div data-testid="duplicate-items">
            <h3 className="text-sm font-semibold text-red-500 uppercase tracking-wide mb-2">Дубли в вашем гардеробе</h3>
            <div className="space-y-2">
                {items.map(item => (
                    <Link key={item.itemId} to={`/clothing/${item.itemId}`}
                        className="block text-sm bg-red-50 dark:bg-red-900/10 rounded-xl p-3 hover:ring-2 hover:ring-brand-500 transition">
                        <span className="font-medium text-ink-800 dark:text-ink-100">{item.name || 'Вещь из гардероба'}</span>
                        <span className="text-brand-500 ml-2">открыть →</span>
                    </Link>
                ))}
            </div>
        </div>
    );
}

interface ResultCardProps {
    result: AnalysisResult;
    onClose: () => void;
    onAddToShopping: () => Promise<void>;
    addingToShopping: boolean;
    addedToShopping: boolean;
}

function ResultCard({result, onClose, onAddToShopping, addingToShopping, addedToShopping}: ResultCardProps) {
    const ai = result.aiResponse;
    const vc = VERDICT_CONFIG[result.purchaseVerdict ?? ''] ?? {label: result.purchaseVerdict ?? '', color: '', bg: 'bg-ink-50 dark:bg-ink-800'};
    const imageKey = imageKeyFromUrl(result.imageUrl);
    return (
        <div className="bg-white dark:bg-ink-900 rounded-2xl shadow p-6 space-y-5">
            {imageKey && (
                <div data-testid="analysis-photo" className="rounded-xl overflow-hidden bg-ink-50 dark:bg-ink-800">
                    <AuthImage imageKey={imageKey} alt="Фото анализируемой вещи" variant="medium"
                        className="w-full max-h-64 object-contain"/>
                </div>
            )}
            <div className="flex items-start justify-between gap-4">
                <div className={`flex-1 rounded-xl p-4 ${vc.bg}`}>
                    <div className="flex items-center gap-3 flex-wrap">
                        <span className={`text-xl font-bold ${vc.color}`}>{vc.label}</span>
                        {result.purchaseScore !== null && (
                            <span className="text-sm text-ink-500">Балл: {result.purchaseScore}/100</span>
                        )}
                        {ai?.wardrobeRoi !== undefined && (
                            <span className={`text-sm font-medium ${ai.wardrobeRoi >= 0
                                ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                ROI: {ai.wardrobeRoi > 0 ? '+' : ''}{ai.wardrobeRoi}
                            </span>
                        )}
                    </div>
                    {result.summary && <p className="mt-2 text-sm text-ink-700 dark:text-ink-300">{result.summary}</p>}
                    {ai?.recommendation?.text && (
                        <p className="mt-2 text-sm font-medium text-ink-900 dark:text-ink-100">{ai.recommendation.text}</p>
                    )}
                </div>
                <button onClick={onClose} className="text-ink-400 hover:text-ink-600 text-2xl leading-none">×</button>
            </div>

            {ai?.detectedItem && (
                <div>
                    <h3 className="text-sm font-semibold text-ink-500 dark:text-ink-400 uppercase tracking-wide mb-2">Определённая вещь</h3>
                    <div className="flex flex-wrap gap-2 text-sm">
                        {[
                            ai.detectedItem.category, ai.detectedItem.color,
                            ai.detectedItem.style, ai.detectedItem.formality,
                            ...(ai.detectedItem.season ?? [])
                        ].filter(Boolean).map((tag, i) => (
                            <span key={i} className="bg-ink-100 dark:bg-ink-800 text-ink-700 dark:text-ink-300 rounded-full px-3 py-1">{tag}</span>
                        ))}
                    </div>
                </div>
            )}

            <ProsConsSection pros={result.pros} cons={result.cons}/>

            {ai?.gapAnalysis && (
                <div className={`rounded-xl p-3 text-sm ${ai.gapAnalysis.closesGap ? 'bg-green-50 dark:bg-green-900/20' : 'bg-ink-50 dark:bg-ink-800'}`}>
                    <span className="font-semibold text-ink-800 dark:text-ink-100">
                        {ai.gapAnalysis.closesGap ? '✓ Закрывает пробел' : '✗ Пробел не закрывает'}
                    </span>
                    {ai.gapAnalysis.gapName && <span className="text-ink-600 dark:text-ink-400">: {ai.gapAnalysis.gapName}</span>}
                </div>
            )}

            <DuplicateItemsSection items={result.duplicateItems}/>

            {ai?.duplicateAnalysis?.isDuplicate && ai.duplicateAnalysis.matchedItems.length > 0 && (
                <div>
                    <h3 className="text-sm font-semibold text-red-500 uppercase tracking-wide mb-2">Похожие вещи в гардеробе</h3>
                    <div className="space-y-2">
                        {ai.duplicateAnalysis.matchedItems.map((m, i) => (
                            <div key={i} className="text-sm bg-red-50 dark:bg-red-900/10 rounded-xl p-3">
                                <span className="font-medium text-ink-800 dark:text-ink-100">{m.description}</span>
                                <span className="text-ink-500"> ({Math.round(m.similarity * 100)}% схожесть)</span>
                                <p className="text-ink-600 dark:text-ink-400 mt-0.5">{m.reason}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {ai?.outfitPotential && (
                <div className="bg-ink-50 dark:bg-ink-800 rounded-xl p-3 text-sm flex gap-6">
                    <div className="text-center">
                        <div className="text-2xl font-bold text-ink-900 dark:text-white">{ai.outfitPotential.newOutfitsCount}</div>
                        <div className="text-xs text-ink-500">новых образов</div>
                    </div>
                    <div className="text-center">
                        <div className="text-2xl font-bold text-ink-900 dark:text-white">{ai.outfitPotential.compatibleItemsCount}</div>
                        <div className="text-xs text-ink-500">совместимых вещей</div>
                    </div>
                </div>
            )}

            {ai?.retirementCandidates && ai.retirementCandidates.length > 0 && (
                <div>
                    <h3 className="text-sm font-semibold text-orange-500 uppercase tracking-wide mb-2">Кандидаты на выбытие</h3>
                    <div className="space-y-1">
                        {ai.retirementCandidates.map((c, i) => (
                            <div key={i} className="text-sm text-ink-700 dark:text-ink-300">
                                <span className="font-medium">{c.description}</span> — {c.reason}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {result.budgetImpact && (
                <div className="bg-ink-50 dark:bg-ink-800 rounded-xl p-3 text-sm" data-testid="budget-impact">
                    <h3 className="text-sm font-semibold text-ink-500 dark:text-ink-400 uppercase tracking-wide mb-1">Бюджет</h3>
                    <p className="text-ink-700 dark:text-ink-300">{result.budgetImpact}</p>
                </div>
            )}

            {/* Decision region: одна primary-кнопка (styleguide) — «В список покупок». */}
            <div className="flex flex-wrap items-center gap-3 pt-1" data-testid="analysis-actions">
                {addedToShopping ? (
                    <Link to="/shopping" data-testid="open-shopping-link"
                        className="inline-flex items-center gap-1.5 min-h-[44px] px-4 rounded-xl text-sm font-semibold
                                   bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400">
                        ✓ В списке покупок — открыть список →
                    </Link>
                ) : (
                    <button onClick={onAddToShopping} disabled={addingToShopping}
                        data-testid="add-to-shopping"
                        className="inline-flex items-center gap-2 min-h-[44px] px-5 rounded-xl bg-brand-500
                                   hover:bg-brand-600 disabled:opacity-40 text-white text-sm font-semibold transition">
                        {addingToShopping ? <InlineSpinner/> : <CartIcon className="w-4 h-4"/>}
                        В список покупок
                    </button>
                )}
                <button onClick={onClose}
                    className="min-h-[44px] px-4 rounded-xl text-sm font-medium text-ink-700 dark:text-ink-300
                               bg-ink-100 dark:bg-ink-800 hover:bg-ink-200 dark:hover:bg-ink-700 transition">
                    Новый анализ
                </button>
            </div>
        </div>
    );
}

function HistoryItem({item, onClick}: {item: AnalysisResult; onClick: () => void}) {
    const vc = VERDICT_CONFIG[item.purchaseVerdict ?? ''];
    return (
        <button onClick={onClick} className="w-full text-left bg-white dark:bg-ink-900 rounded-xl p-4 flex gap-3 hover:ring-2 hover:ring-brand-500 transition">
            {item.imageUrl && (
                <img src={item.imageUrl} alt="вещь" className="w-14 h-18 object-cover rounded-lg shrink-0"/>
            )}
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                    {vc && <span className={`text-sm font-semibold ${vc.color}`}>{vc.label}</span>}
                    {item.purchaseScore !== null && <span className="text-xs text-ink-400">{item.purchaseScore}/100</span>}
                </div>
                {item.summary && <p className="mt-1 text-sm text-ink-600 dark:text-ink-400 line-clamp-2">{item.summary}</p>}
                <p className="mt-1 text-xs text-ink-400">{formatDateTime(item.createdAt)}</p>
            </div>
        </button>
    );
}

const PurchaseAnalysisPage: React.FC = () => {
    useDocumentTitle();
    const fileRef = useRef<HTMLInputElement>(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const [preview, setPreview] = useState<string | null>(null);
    const [base64, setBase64] = useState<string | null>(null);
    const [comment, setComment] = useState('');
    const [categoryHint, setCategoryHint] = useState('');
    const [priceRub, setPriceRub] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState<AnalysisResult | null>(null);
    const [history, setHistory] = useState<AnalysisResult[] | null>(null);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [addingToShopping, setAddingToShopping] = useState(false);
    const [addedToShopping, setAddedToShopping] = useState(false);

    const handleFile = useCallback((file: File) => {
        const reader = new FileReader();
        reader.onload = e => {
            const dataUrl = e.target?.result as string;
            setPreview(dataUrl);
            setBase64(dataUrl.split(',')[1]);
        };
        reader.readAsDataURL(file);
    }, []);

    const onDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        if (file?.type.startsWith('image/')) handleFile(file);
    }, [handleFile]);

    const submit = async () => {
        if (!base64) return;
        setSubmitting(true);
        setError(null);
        try {
            const api = await createAxiosInstance();
            const res = await api.post<AnalysisResult>('/purchase-analysis', {
                photoBase64: base64,
                comment: comment || undefined,
                categoryHint: categoryHint || undefined,
                priceRub: priceRub ? Number(priceRub) : undefined,
            });
            showResult(normalizeResult(res.data));
        } catch (err: unknown) {
            const axErr = err as {response?: {data?: {message?: string; error?: string}}};
            const serverMsg = axErr?.response?.data?.message || axErr?.response?.data?.error;
            setError(serverMsg || 'Ошибка при анализе. Попробуйте ещё раз.');
        } finally {
            setSubmitting(false);
        }
    };

    const loadHistory = useCallback(async () => {
        setLoadingHistory(true);
        try {
            const api = await createAxiosInstance();
            const res = await api.get<{content: AnalysisResult[]}>('/purchase-analysis/history');
            const items = res.data.content ?? [];
            items.forEach(normalizeResult);
            setHistory(items);
        } catch {
            setHistory([]);
        } finally {
            setLoadingHistory(false);
        }
    }, []);

    /** Анализ → пункт списка покупок (идемпотентный POST, FR-10). */
    const addToShopping = async () => {
        if (!result) return;
        setAddingToShopping(true);
        try {
            const api = await createAxiosInstance();
            await api.post(`/purchase-analysis/${result.id}/to-shopping-list`);
            setAddedToShopping(true);
        } catch {
            setError('Не удалось добавить в список покупок. Попробуйте ещё раз.');
        } finally {
            setAddingToShopping(false);
        }
    };

    const showResult = (r: AnalysisResult) => {
        setAddedToShopping(!!r.shoppingItemId);
        setError(null);
        setResult(r);
    };

    // Открытие сохранённого отчёта по ссылке из списка покупок: /purchase-analysis?id=<analysisId>.
    const openById = useCallback(async (id: string) => {
        try {
            const api = await createAxiosInstance();
            const res = await api.get<AnalysisResult>(`/purchase-analysis/${id}`);
            const r = normalizeResult(res.data);
            setAddedToShopping(!!r.shoppingItemId);
            setResult(r);
        } catch {
            setError('Не удалось открыть анализ. Возможно, он был удалён.');
        }
    }, []);

    useEffect(() => {
        const id = searchParams.get('id');
        if (id) openById(id);
    }, [searchParams, openById]);

    const reset = () => {
        setPreview(null); setBase64(null); setComment('');
        setCategoryHint(''); setPriceRub(''); setResult(null); setError(null);
        setAddedToShopping(false);
        if (searchParams.get('id')) setSearchParams({}, {replace: true});
    };

    return (
        <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
            <div className="flex items-center gap-2">
                <CartIcon className="w-6 h-6 text-brand-500"/>
                <h1 className="text-2xl font-bold text-ink-900 dark:text-white">Анализ покупки</h1>
            </div>

            {result ? (
                <>
                    <ResultCard result={result} onClose={reset}
                        onAddToShopping={addToShopping}
                        addingToShopping={addingToShopping}
                        addedToShopping={addedToShopping}/>
                    {error && <p className="text-sm text-red-500">{error}</p>}
                </>
            ) : (
                <div className="bg-white dark:bg-ink-900 rounded-2xl shadow p-6 space-y-4">
                    <div
                        onDrop={onDrop} onDragOver={e => e.preventDefault()}
                        onClick={() => fileRef.current?.click()}
                        className="border-2 border-dashed border-ink-300 dark:border-ink-600 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-brand-500 transition min-h-[180px]"
                    >
                        {preview ? (
                            <img src={preview} alt="preview" className="max-h-64 rounded-xl object-contain"/>
                        ) : (
                            <div className="text-center p-6">
                                <CartIcon className="w-10 h-10 text-ink-300 dark:text-ink-600 mx-auto mb-2"/>
                                <p className="text-sm text-ink-500 dark:text-ink-400">Нажмите или перетащите фото вещи</p>
                                <p className="text-xs text-ink-400 mt-1">Фото в магазине, с сайта или на вас</p>
                            </div>
                        )}
                    </div>
                    <input ref={fileRef} type="file" accept="image/*" className="hidden"
                        onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); }}/>

                    <textarea
                        value={comment} onChange={e => setComment(e.target.value)}
                        placeholder="Почему рассматриваете покупку? Что смущает? (необязательно)"
                        className="w-full rounded-xl border border-ink-200 dark:border-ink-700 bg-ink-50 dark:bg-ink-800 text-ink-900 dark:text-white p-3 text-sm resize-none h-20 focus:outline-none focus:ring-2 focus:ring-brand-500"
                    />

                    <div className="flex gap-3">
                        <input value={categoryHint} onChange={e => setCategoryHint(e.target.value)}
                            placeholder="Категория (худи, джинсы…)"
                            className="flex-1 rounded-xl border border-ink-200 dark:border-ink-700 bg-ink-50 dark:bg-ink-800 text-ink-900 dark:text-white p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"/>
                        <input type="number" value={priceRub} onChange={e => setPriceRub(e.target.value)}
                            placeholder="Цена ₽"
                            className="w-28 rounded-xl border border-ink-200 dark:border-ink-700 bg-ink-50 dark:bg-ink-800 text-ink-900 dark:text-white p-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"/>
                    </div>

                    {error && <p className="text-sm text-red-500">{error}</p>}

                    <button onClick={submit} disabled={!base64 || submitting}
                        className="w-full py-3 rounded-xl bg-brand-500 hover:bg-brand-600 disabled:opacity-40 text-white font-semibold transition flex items-center justify-center gap-2">
                        {submitting ? <><InlineSpinner/><span>Анализирую…</span></> : <><SparklesIcon className="w-5 h-5"/><span>Стоит ли покупать?</span></>}
                    </button>
                </div>
            )}

            <div>
                {history === null ? (
                    <button onClick={loadHistory} className="text-sm text-brand-500 hover:underline">
                        Показать историю анализов
                    </button>
                ) : loadingHistory ? (
                    <InlineSpinner/>
                ) : history.length === 0 ? (
                    <p className="text-sm text-ink-400">История пуста</p>
                ) : (
                    <div className="space-y-3">
                        <h2 className="text-sm font-semibold text-ink-500 dark:text-ink-400 uppercase tracking-wide">История</h2>
                        {history.map(item => (
                            <HistoryItem key={item.id} item={item} onClick={() => showResult(item)}/>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PurchaseAnalysisPage;
